import { toast } from 'sonner';
import { SessionExpiredError } from '@/context/AuthSessionContext';
import { t } from '@/i18n';

/**
 * File downloads — the ONE saving primitive for the whole app.
 *
 * WHY this exists (2026-07-31): downloads used to open a new tab pointed at a
 * presigned S3 URL (`window.open` reserved in the click handler, redirected
 * after the await). In some hosts — corporate popup policies, extensions,
 * Safari settings — `window.open` returns null EVEN with pop-ups allowed for
 * the site, and the refusal is silent: the server logs a 200, the user sees a
 * dead button. On top of that the S3 objects carry no Content-Disposition, so
 * even a successfully opened tab only DISPLAYED the PDF and never saved it.
 *
 * The bucket has no CORS configuration and infrastructure may not change, so
 * the browser cannot `fetch` S3 directly either. The only robust path is the
 * one the fiscal/XML/ZIP exports already use in production: stream the bytes
 * through OUR OWN origin (authenticated API endpoint that proxies S3 and sets
 * Content-Disposition) and save them with a synthetic anchor click — which an
 * empirical Playwright/WebKit matrix showed is immune to popup policy.
 */

/** Triggers a browser download for an in-memory blob (XML/UPO/PDF exports). */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Makes a string safe for use inside a filename: slashes, backslashes and
 * whitespace runs become single dashes ("UZ 001/07/2026" → "UZ-001-07-2026"),
 * characters filesystems refuse are stripped.
 */
export function sanitizeFileNamePart(part: string): string {
  return part
    .replace(/[/\\\s]+/g, '-')
    .replace(/[<>:"|?*]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '');
}

/** Best-effort extension for blobs whose stored filename is unknown (scans may be PDFs or images). */
const MIME_EXTENSIONS: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/gif': 'gif',
  'image/tiff': 'tiff',
};

/**
 * Fetches a file through our authenticated API and saves it as `filename`.
 *
 * @param getBlob   an authenticated blob-fetch (e.g. `useApi().getBlob`)
 * @param endpoint  API endpoint streaming the file bytes
 * @param filename  target filename; when it has no extension one is inferred
 *                  from the response MIME type (returned scans may be PDFs or
 *                  photos and the stored name is not always known up front)
 *
 * Errors: a `SessionExpiredError` is re-thrown so the global session-expired
 * modal fires; anything else surfaces as a `common.downloadFailed` toast and
 * is swallowed (callers only need try/catch for their own busy state).
 */
export async function downloadViaApi(
  getBlob: (endpoint: string) => Promise<Blob>,
  endpoint: string,
  filename: string,
): Promise<void> {
  let blob: Blob;
  try {
    blob = await getBlob(endpoint);
  } catch (error) {
    if (error instanceof SessionExpiredError) throw error;
    console.error('Download failed:', endpoint, error);
    toast.error(t('common.downloadFailed'));
    return;
  }
  let name = filename;
  if (!/\.[A-Za-z0-9]{2,5}$/.test(name)) {
    const ext = MIME_EXTENSIONS[blob.type.split(';')[0].trim().toLowerCase()];
    if (ext) name = `${name}.${ext}`;
  }
  downloadBlob(blob, name);
}
